import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import AddEditRoadMapValidation from '../../../../../validation/AddEditRoadMapValidation';
import { StandardButton } from '../../../../../component/common/StandardUI';

const emptyColumn = () => ({
  id: `${Date.now()}-${Math.random()}`,
  _id: null,
  period: '',
  heading: '',
  description: '',
  status: 'pending'
});

const AddEditRoadMap = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([emptyColumn()]);

  const formik = useFormik({
    initialValues: {
      heading: '',
      description: ''
    },
    validationSchema: AddEditRoadMapValidation,
    validateOnBlur: false,
    onSubmit: () => { }
  });

  const updateColumn = (columnId, field, value) => {
    setColumns((prev) =>
      prev.map((col) => (col.id === columnId ? { ...col, [field]: value } : col))
    );
  };

  const addColumn = () => setColumns((prev) => [...prev, emptyColumn()]);

  const removeColumn = (columnId) =>
    setColumns((prev) => (prev.length > 1 ? prev.filter((col) => col.id !== columnId) : prev));

  const submitRoadMap = async (saveAsDraft) => {
    const errors = await formik.validateForm();
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      // touch every errored field so the block is always visible — a field that
      // validates but isn't rendered would otherwise stall the save silently
      formik.setTouched(Object.fromEntries(errorFields.map((field) => [field, true])));
      errorToast(errors[errorFields[0]] || 'Please complete the required fields.');
      return;
    }

    const items = columns
      .filter((col) => col.period || col.heading || col.description)
      .map(({ _id, period, heading, description, status }) => ({
        // existing columns round-trip their _id/status so a save doesn't reset
        // columns that were already marked completed
        ...(_id ? { _id, status } : {}),
        period,
        heading,
        description
      }));

    if (items.some((item) => !item.heading?.trim())) {
      errorToast('Every column needs a heading.');
      return;
    }

    const payload = {
      title: formik.values.heading,
      description: formik.values.description,
      items,
      saveAsDraft
    };

    try {
      setLoading(true);
      const response = isEdit
        ? await CompanyApi.updateRoadMap(id, payload)
        : await CompanyApi.createRoadMap(payload);
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Road map saved successfully');
        navigate(`/${slug}/people-plan/roadmaps`);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to save road map');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to save road map');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoadMap = async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.getRoadMapById(id);
      if (response?.data?.meta?.code === 1) {
        const roadMap = response?.data?.data;
        formik.setValues({
          heading: roadMap?.title || '',
          description: roadMap?.description || ''
        });
        if (Array.isArray(roadMap?.items) && roadMap.items.length > 0) {
          setColumns(
            roadMap.items.map((col) => ({
              id: `${Date.now()}-${Math.random()}`,
              _id: col._id || null,
              period: col.period || '',
              heading: col.heading || '',
              description: col.description || '',
              status: col.status || 'pending'
            }))
          );
        } else if (roadMap?.date) {
          // Bridges older flat-model road maps (single date, no columns) until the
          // backend adds column support.
          setColumns([{ ...emptyColumn(), period: moment(roadMap.date).format('MMM YYYY') }]);
        }
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to load road map');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load road map');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCopanySubAdmin()) {
      errorToast('You do not have permission to manage road maps.');
      navigate(`/${slug}/people-plan/roadmaps`);
      return;
    }
    if (isEdit) fetchRoadMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Road Maps', href: `/${slug}/people-plan/roadmaps`, current: false },
    { name: isEdit ? 'Edit Road Map' : 'New Road Map', href: '#', current: true }
  ];

  return (
    <div className="relative lg:mb-[45px]">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>{isEdit ? 'Edit Road Map' : 'New Road Map'} - People plan | Shoorah Admin</title>
      </Helmet>
      <Topbar title={isEdit ? 'Edit Road Map' : 'New Road Map'} infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6"></div>

      <div className="lg:mt-10 mt-6 rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder sm:p-6 p-4 space-y-6">
        <div className="space-y-3">
          <input
            type="text"
            name="heading"
            value={formik.values.heading}
            onChange={formik.handleChange}
            placeholder="Insert heading"
            className="w-full block bg-transparent text-lg font-medium border-none px-0 focus:outline-none focus:ring-0 dark:text-white"
          />
          {formik.touched.heading && formik.errors.heading && (
            <p className="text-red-500 text-xs">{formik.errors.heading}</p>
          )}
          <textarea
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            placeholder="Add description..."
            rows={2}
            className="w-full block bg-transparent text-sm text-gray-500 dark:text-gray-300 border-none px-0 resize-none focus:outline-none focus:ring-0"
          />
          {formik.touched.description && formik.errors.description && (
            <p className="text-red-500 text-xs">{formik.errors.description}</p>
          )}
        </div>

        <div className="border-t border-shoorah-borderGray dark:border-shoorah-darkBorder pt-5">
          <div className="flex items-stretch gap-4 overflow-x-auto pb-2">
            {columns.map((col) => (
              <div key={col.id} className="w-[240px] shrink-0 flex flex-col gap-3 relative">
                {columns.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeColumn(col.id)}
                    className="absolute -top-1 -right-1 text-gray-400 hover:text-red-500"
                    aria-label="Remove period"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
                <input
                  type="text"
                  value={col.period}
                  onChange={(e) => updateColumn(col.id, 'period', e.target.value)}
                  placeholder="Insert date"
                  className="w-full rounded-input border border-gray-300 dark:border-gray-600 bg-transparent px-4 py-2 text-sm text-center dark:text-white focus:outline-none focus:border-shoorah-btn dark:focus:border-shoorah-btnDark"
                />
                <div className="rounded-input bg-shoorah-normanGray dark:bg-shoorah-darkBgColor p-3">
                  <input
                    type="text"
                    value={col.heading}
                    onChange={(e) => updateColumn(col.id, 'heading', e.target.value)}
                    placeholder="Insert heading"
                    className="w-full bg-transparent text-sm font-medium border-none px-0 focus:outline-none focus:ring-0 dark:text-white"
                  />
                </div>
                <textarea
                  value={col.description}
                  onChange={(e) => updateColumn(col.id, 'description', e.target.value)}
                  placeholder="Add description..."
                  rows={6}
                  className="w-full flex-1 rounded-input bg-shoorah-normanGray dark:bg-shoorah-darkBgColor p-3 text-sm border-none resize-none focus:outline-none focus:ring-0 dark:text-white"
                />
              </div>
            ))}

            <button
              type="button"
              onClick={addColumn}
              className="shrink-0 self-start mt-1 w-9 h-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 hover:text-shoorah-primary hover:border-shoorah-btn dark:hover:border-shoorah-btnDark"
              aria-label="Add period"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <StandardButton text="Cancel" variant="secondary" onClick={() => navigate(-1)} />
          <StandardButton
            text="Save as Draft"
            variant="secondary"
            disabled={loading}
            onClick={() => submitRoadMap(true)}
          />
          <StandardButton
            text={isEdit ? 'Save Changes' : 'Save'}
            disabled={loading}
            onClick={() => submitRoadMap(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default AddEditRoadMap;
